import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import colors from '../../../styles/colors';
import { useAppSelector } from '../../../store/hooks';
import { apiGetMyCreditHistory } from '../../../api/userCardApi';
import type { CreditHistoryEntry } from '../../../api/userCardApi';

const Container = styled.div`
  padding: 20px 16px 70px;
  min-height: calc(100vh - 70px);
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: gold;
  margin: 0 0 20px;
`;

const EntryCard = styled.div`
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
`;

const EntryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const DateText = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
`;

const AmountText = styled.span<{ $positive: boolean }>`
  font-size: 16px;
  font-weight: 700;
  color: ${({ $positive }) => ($positive ? '#4caf6f' : '#e05555')};
`;

const NotesText = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
`;

const AdminText = styled.p`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.3);
  margin: 4px 0 0;
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 32px;
  font-size: 14px;
`;

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
};

const CreditHistoryMobile: React.FC = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(
    (state) => state.auth.isAuthenticated || state.auth.userLoggedState !== null
  );
  const [entries, setEntries] = useState<CreditHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/carte');
      return;
    }
    apiGetMyCreditHistory()
      .then(setEntries)
      .catch(() => setEntries([]))
      .finally(() => setIsLoading(false));
  }, [isAuthenticated, navigate]);

  return (
    <Container>
      <Title>Storico Movimenti</Title>

      {isLoading && <StatusText>Caricamento...</StatusText>}

      {!isLoading && entries.length === 0 && (
        <StatusText>Nessun movimento registrato.</StatusText>
      )}

      {!isLoading && entries.map((e) => (
        <EntryCard key={e.id}>
          <EntryRow>
            <DateText>{formatDate(e.created_at)}</DateText>
            <AmountText $positive={e.amount > 0}>
              {e.amount > 0 ? '+' : ''}{e.amount}
            </AmountText>
          </EntryRow>
          <NotesText>{e.notes ?? '—'}</NotesText>
          {e.granted_by && (
            <AdminText>Admin: {e.granted_by.name}</AdminText>
          )}
        </EntryCard>
      ))}
    </Container>
  );
};

export default CreditHistoryMobile;
